import type { OrganizationUserRole } from '@ent/permissions-shared';
import {
  defaultOrganizationRoleForPosition,
  isLeadershipKitchenPosition
} from '@utils/kitchen-leadership-positions';

/** Product policy for UI visibility. Ent privacy remains authoritative. */
export const CAPABILITIES = {
  ManageOrganization: 'organization.manage',
  ManageTeam: 'team.manage',
  ExportTeam: 'team.export',
  ManageUnavailableItems: 'unavailable-items.manage',
  // Admin-only stockout history and analytics. src/zero/query-authz.ts
  // derives its role list from this table with rolesWith().
  ViewUnavailableItemAnalytics: 'unavailable-items.analytics',
  ManageMenus: 'menus.manage',
  ManageRecipes: 'recipes.manage',
  ManageReference: 'reference.manage',
  // Adding items to the Wine & Spirits catalog. Mirrors the ent `create`
  // permission on inventory_items, which managers don't hold.
  ManageInventory: 'inventory.manage',
  CreateLogbookEntry: 'logbook.create',
  ManageChannels: 'channels.manage',
  SendMessages: 'messages.send',
  // Adding/removing/configuring agent identities in channels/DMs, and
  // reading `agent_profiles`. See docs/plans/2026-09-17-agentic-rag-and-conversations.md
  // Phase B/C. Not granted to hr/employee_lead/employee.
  ManageAgents: 'agents.manage'
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

const ALL_CAPABILITIES = Object.values(CAPABILITIES);
const MANAGEMENT_CAPABILITIES = ALL_CAPABILITIES.filter(
  capability => capability !== CAPABILITIES.ViewUnavailableItemAnalytics
);

export const ROLE_CAPABILITIES: Record<
  OrganizationUserRole,
  readonly Capability[]
> = {
  admin: ALL_CAPABILITIES,
  owner: MANAGEMENT_CAPABILITIES,
  gm: MANAGEMENT_CAPABILITIES,
  mgr: MANAGEMENT_CAPABILITIES.filter(
    capability =>
      capability !== CAPABILITIES.ManageOrganization &&
      capability !== CAPABILITIES.ManageInventory
  ),
  hr: [
    CAPABILITIES.ManageTeam,
    CAPABILITIES.ManageUnavailableItems,
    CAPABILITIES.SendMessages
  ],
  employee_lead: [
    CAPABILITIES.ManageUnavailableItems,
    CAPABILITIES.SendMessages
  ],
  employee: [CAPABILITIES.ManageUnavailableItems, CAPABILITIES.SendMessages]
};

/** Roles from most to least privileged, for picking one row per user. */
export const ROLE_PRECEDENCE: readonly OrganizationUserRole[] = [
  'owner',
  'admin',
  'gm',
  'mgr',
  'hr',
  'employee_lead',
  'employee'
];

/** The most privileged of `roles`, or `undefined` when none is known. */
export function primaryRole(
  roles: readonly (OrganizationUserRole | null | undefined)[]
): OrganizationUserRole | undefined {
  let best: OrganizationUserRole | undefined;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const role of roles) {
    if (!role) continue;
    const rank = ROLE_PRECEDENCE.indexOf(role);
    if (rank !== -1 && rank < bestRank) {
      best = role;
      bestRank = rank;
    }
  }
  return best;
}

export function hasCapability(
  role: OrganizationUserRole | null | undefined,
  capability: Capability,
  explicitGrants: readonly Capability[] = []
): boolean {
  if (explicitGrants.includes(capability)) return true;
  return role ? ROLE_CAPABILITIES[role].includes(capability) : false;
}

/**
 * The roles that hold `capability`, in `ROLE_CAPABILITIES` order. Row
 * predicates and server policies that once hand-copied a role list derive
 * it here, so a change to the table reaches them.
 */
export function rolesWith(capability: Capability): OrganizationUserRole[] {
  return (Object.keys(ROLE_CAPABILITIES) as OrganizationUserRole[]).filter(
    role => ROLE_CAPABILITIES[role].includes(capability)
  );
}

/**
 * Kitchen leadership positions (`chef`, `sous_chef`, etc.) store at least
 * `mgr` on the membership row. Keeps owner/gm/admin when already higher.
 */
export function organizationRoleForPosition(
  requestedRole: OrganizationUserRole,
  position: string | null | undefined
): OrganizationUserRole {
  if (!isLeadershipKitchenPosition(position)) return requestedRole;
  if (roleRank(requestedRole) >= roleRank('mgr')) return 'mgr';
  return requestedRole;
}

/** Position in `ROLE_PRECEDENCE`; lower is more privileged, unknown is last. */
function roleRank(role: OrganizationUserRole | null | undefined): number {
  const rank = role ? ROLE_PRECEDENCE.indexOf(role) : -1;
  return rank === -1 ? ROLE_PRECEDENCE.length : rank;
}

/**
 * Roles `actorRole` may hand out. Owners, admins and GMs change roles, but
 * never to one more privileged than their own.
 */
export function assignableRoles(
  actorRole: OrganizationUserRole | null | undefined
): OrganizationUserRole[] {
  if (!hasCapability(actorRole, CAPABILITIES.ManageOrganization)) return [];
  const rank = roleRank(actorRole);
  return ROLE_PRECEDENCE.filter(role => roleRank(role) >= rank);
}

/**
 * True when `actorRole` may change the role and position of a member whose
 * most privileged role is `memberRole`: a GM can't touch an admin or owner.
 */
export function canManageMember(
  actorRole: OrganizationUserRole | null | undefined,
  memberRole: OrganizationUserRole | null | undefined
): boolean {
  if (!hasCapability(actorRole, CAPABILITIES.ManageOrganization)) return false;
  return roleRank(memberRole) >= roleRank(actorRole);
}

/**
 * True when `actorRole` may invite someone at `role` for `position`: a role
 * they can assign, or the position's usual role when it is not above their
 * own (managers and HR adding staff). Also decides who may resend an
 * invitation to a member at that role.
 */
export function canInviteWithRole(
  actorRole: OrganizationUserRole | null | undefined,
  role: OrganizationUserRole,
  position: string | null | undefined
): boolean {
  if (!actorRole) return false;
  return (
    assignableRoles(actorRole).includes(role) ||
    (role === defaultOrganizationRoleForPosition(position) &&
      roleRank(role) >= roleRank(actorRole))
  );
}

/**
 * True when `actorRole` may take a member whose most privileged role is
 * `memberRole` out of the workspace. Anyone who manages the team removes
 * members below their own rank; owners, admins and GMs also remove peers,
 * as in `canManageMember`. Nobody removes themselves this way.
 */
export function canRemoveMember(
  actorRole: OrganizationUserRole | null | undefined,
  memberRole: OrganizationUserRole | null | undefined
): boolean {
  if (!hasCapability(actorRole, CAPABILITIES.ManageTeam)) return false;
  return (
    canManageMember(actorRole, memberRole) ||
    roleRank(memberRole) > roleRank(actorRole)
  );
}

/** True when switching from `from` to `to` takes away a capability. */
export function losesCapabilities(
  from: OrganizationUserRole | null | undefined,
  to: OrganizationUserRole | null | undefined
): boolean {
  const kept = new Set<Capability>(to ? ROLE_CAPABILITIES[to] : []);
  return (from ? ROLE_CAPABILITIES[from] : []).some(
    capability => !kept.has(capability)
  );
}
